import React, { useState, useEffect } from "react";
import Select from "../../ui/PortalSelect";
import "antd/dist/antd.css";
import { DatePicker } from "antd";
import Swal from "sweetalert2";
import moment from "moment";
import axios from "axios";
import { baseURL } from "../backend_url";
import { loadAccessibleCompanies, readSessionUser } from '../accountAccess';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  Grid
} from "@mui/material";

export default function DetailedClickUtilizationReport() {

  const [companyDetail, setCompanyDetail] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");

  const [locationDetail, setLocationDetail] = useState([]);
  const [locationName, setLocationName] = useState("");
  const [locationID, setLocationID] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [loggedInUserDetail, setLoggedInUserDetail] = useState({});

  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [fetchClicked, setFetchClicked] = useState(false);

  const [reportData, setReportData] = useState({
    account_name: "",
    total_clicks_account: 0,
    total_actions_account: 0,
    timezone: "IST",
    locations: []
  });

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedLocationRow, setSelectedLocationRow] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userRows, setUserRows] = useState([]);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUserRow, setSelectedUserRow] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsRows, setDetailsRows] = useState([]);

  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [selectedCriteriaRow, setSelectedCriteriaRow] = useState(null);

  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  const fetchCompanyDetails = async () => {
    try {
      setLoadingCompany(true);

      const { user: foundUser, error } = readSessionUser();
      if (!foundUser) {
        setCompanyDetail([]);
        throw new Error(error);
      }

      setLoggedInUserDetail(foundUser);
      const permissionKey = foundUser?.access?.["Reports_Account Access"]
        ? "Reports_Account Access"
        : "Adjustment_Account Access";
      const accounts = await loadAccessibleCompanies(
        baseURL,
        foundUser,
        permissionKey
      );
      setCompanyDetail(accounts);
    } catch (error) {
      setCompanyDetail([]);
      Swal.fire("Unable to fetch account details");
    } finally {
      setLoadingCompany(false);
    }
  };

  const fetchLocationsForCompany = async (companyObj) => {
    try {
      if (!companyObj || !companyObj._id) return;

      setLoadingLocations(true);
      setLocationDetail([]);
      setLocationName("");
      setLocationID("");

      const companyMongoId = companyObj._id;
      setCompanyId(companyObj.account_id || companyObj.company_id || companyObj._id || "");

      const res = await axios.get(
        baseURL + "/get_company_location/" + companyMongoId
        + "?companyNumber=" + encodeURIComponent(companyMongoId.toString())
      );

      let allLocations = res?.data?.res || [];

      if (loggedInUserDetail.role_capabilities?.all_modules_access === true) {
        setLocationDetail(allLocations);
      } else {
        const userRes = await axios.post(baseURL + "/get_individual_user_details", {
          companyNumber: companyMongoId.toString(),
          user_email: loggedInUserDetail.userEmail
        });

        const allocatedLocations = userRes?.data?.[0]?.location_allocated || {};

        allLocations = allLocations.filter(
          (val) =>
            val !== undefined &&
            allocatedLocations &&
            allocatedLocations[val.location_id]
        );

        setLocationDetail(allLocations);
      }
    } catch (error) {
      console.error("Location fetch error:", error);
      Swal.fire("Unable to fetch location details");
    } finally {
      setLoadingLocations(false);
    }
  };

  const companyNameOptions = companyDetail
    .filter((value) => value && value.company_name)
    .map((value) => ({
      value: value.company_name,
      label: value.company_name
    }));

  const locationNameOptions = locationDetail
    .filter((value) => value && value.location_id)
    .map((value) => ({
      value: value.location_id,
      label: value.location_name
    }));

  const getAccountIdForReport = () => {
    if (!selectedCompany) return "";

    return (
      selectedCompany.account_id ||
      selectedCompany.accountId ||
      selectedCompany.company_id ||
      companyId ||
      selectedCompany._id ||
      ""
    ).toString();
  };

  const getCompanyIdForReport = () => {
    return getAccountIdForReport();
  };

  const resetReport = () => {
    setFetchClicked(false);
    setReportData({
      account_name: "",
      total_clicks_account: 0,
      total_actions_account: 0,
      timezone: "IST",
      locations: []
    });
    setUserRows([]);
    setDetailsRows([]);
    setSelectedLocationRow(null);
    setSelectedUserRow(null);
    setSelectedCriteriaRow(null);
  };

  const handleInputChangeCompanyName = async (characterEntered) => {
    if (!characterEntered) {
      setCompanyName("");
      setSelectedCompany(null);
      setCompanyId("");
      setLocationDetail([]);
      setLocationName("");
      setLocationID("");
      resetReport();
      return;
    }

    const selected = companyDetail.find(
      (value) => value.company_name === characterEntered.value
    );

    setCompanyName(characterEntered.value);
    setSelectedCompany(selected || null);

    resetReport();

    if (selected) {
      await fetchLocationsForCompany(selected);
    }
  };

  const handleInputChangeLocationName = (characterEntered) => {
    if (!characterEntered) {
      setLocationName("");
      setLocationID("");
      resetReport();
      return;
    }

    const selectedLocation = locationDetail.find(
      (value) => value.location_id === characterEntered.value
    );

    setLocationID(characterEntered.value);
    setLocationName(selectedLocation?.location_name || characterEntered.label || "");
    resetReport();
  };

  const handleFetch = async (e) => {
    e.preventDefault();

    if (!selectedCompany || !companyName) {
      Swal.fire("Please select an account to proceed ahead");
      return;
    }

    if (!locationID) {
      Swal.fire("Please select a location to proceed ahead");
      return;
    }

    if (!fromDate) {
      Swal.fire("Please select From Date to proceed ahead");
      return;
    }

    if (!toDate) {
      Swal.fire("Please select To Date to proceed ahead");
      return;
    }

    if (moment(fromDate, "YYYY-MM-DD").isAfter(moment(toDate, "YYYY-MM-DD"))) {
      Swal.fire("From Date cannot be greater than To Date");
      return;
    }

    try {
      setLoadingReport(true);
      setFetchClicked(true);

      const payload = {
        companyNumber: getAccountIdForReport(),
        account_id: getAccountIdForReport(),
        company_id: getCompanyIdForReport(),
        account_name: companyName,

        location_id: locationID,
        location_name: locationName,

        from_date: fromDate,
        to_date: toDate,
        timezone: "Asia/Kolkata"
      };

      const res = await axios.post(
        baseURL + "/get_detailed_click_utilization_report",
        payload
      );

      if (res?.data?.status === "error") {
        Swal.fire(res?.data?.message || "Unable to fetch report");
      }

      setReportData({
        account_name: res?.data?.account_name || companyName,
        total_clicks_account: res?.data?.total_clicks_account || 0,
        total_actions_account: res?.data?.total_actions_account || 0,
        timezone: res?.data?.timezone || "IST",
        locations: res?.data?.locations || []
      });
    } catch (error) {
      console.error("Detailed report fetch error:", error);
      Swal.fire("Unable to fetch detailed click utilization report");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleViewUsers = async (locationRow) => {
    try {
      setSelectedLocationRow(locationRow);
      setUserDialogOpen(true);
      setUserLoading(true);
      setUserRows([]);

      const payload = {
        companyNumber: getAccountIdForReport(),
        account_id: getAccountIdForReport(),
        company_id: getCompanyIdForReport(),
        location_id: locationRow.location_id || locationID,
        location_name: locationRow.location_name || locationName,
        from_date: fromDate,
        to_date: toDate,
        timezone: "Asia/Kolkata"
      };

      const res = await axios.post(
        baseURL + "/get_detailed_click_user_breakdown",
        payload
      );

      if (res?.data?.status === "error") {
        Swal.fire(res?.data?.message || "Unable to fetch user details");
      }

      setUserRows(res?.data?.users || []);
    } catch (error) {
      console.error("User breakdown error:", error);
      Swal.fire("Unable to fetch user-level click details");
    } finally {
      setUserLoading(false);
    }
  };

  const handleViewUserDetails = async (userRow) => {
    try {
      setSelectedUserRow(userRow);
      setDetailsDialogOpen(true);
      setDetailsLoading(true);
      setDetailsRows([]);

      const payload = {
        companyNumber: getAccountIdForReport(),
        account_id: getAccountIdForReport(),
        company_id: getCompanyIdForReport(),
        location_id: selectedLocationRow?.location_id || locationID,
        location_name: selectedLocationRow?.location_name || locationName,
        created_by: userRow.created_by,
        from_date: fromDate,
        to_date: toDate,
        timezone: "Asia/Kolkata"
      };

      const res = await axios.post(
        baseURL + "/get_detailed_click_criteria_details",
        payload
      );

      if (res?.data?.status === "error") {
        Swal.fire(res?.data?.message || "Unable to fetch criteria details");
      }

      setDetailsRows(res?.data?.details || []);
    } catch (error) {
      console.error("Criteria details error:", error);
      Swal.fire("Unable to fetch selected criteria details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewCriteria = (row) => {
    setSelectedCriteriaRow(row);
    setCriteriaDialogOpen(true);
  };

  const formatISTDateTime = (value) => {
    if (!value) return "-";

    const parsed = moment(value, [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss",
      moment.ISO_8601
    ]);

    if (!parsed.isValid()) return value;

    return parsed.format("MM-DD-YYYY hh:mm:ss A");
  };

  const formatISTDate = (value) => {
    if (!value) return "-";

    const parsed = moment(value, ["YYYY-MM-DD", "MM-DD-YYYY", moment.ISO_8601]);

    if (!parsed.isValid()) return value;

    return parsed.format("MM-DD-YYYY");
  };

  const safeJoin = (value) => {
    if (!value) return "-";
    if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";
    return value.toString();
  };

  const getCriteriaRows = (detailRow) => {
    const automationData = detailRow?.automation_data || {};
    const directRules = automationData.car_class_rules;

    if (Array.isArray(directRules) && directRules.length > 0) {
      return directRules;
    }

    const sipp = automationData.sipp_code || [];
    const comp1 = automationData.comp_sipp_code || [];
    const comp2 = automationData.comp_sipp_code2 || [];
    const comp3 = automationData.comp_sipp_code3 || [];
    const strategy = automationData.strategy || [];
    const selectedCompetitor = automationData.selected_competitor || [];
    const selectedChange = automationData.selected_change || [];
    const amount = automationData.amount || [];

    const maxLength = Math.max(
      sipp.length,
      comp1.length,
      comp2.length,
      comp3.length,
      strategy.length,
      selectedCompetitor.length,
      selectedChange.length,
      amount.length
    );

    const rows = [];

    for (let i = 0; i < maxLength; i++) {
      rows.push({
        sipp_code: sipp[i] || "",
        comp_sipp_code: comp1[i] || "",
        comp_sipp_code2: comp2[i] || "",
        comp_sipp_code3: comp3[i] || "",
        strategy: strategy[i] || "",
        selected_competitor: selectedCompetitor[i] || "",
        selected_change: selectedChange[i] || "",
        amount: amount[i] || ""
      });
    }

    return rows;
  };

  const summaryCardStyle = {
    height: "100%",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    borderRadius: "10px"
  };

  const tableHeaderCell = {
    fontWeight: "bold",
    border: "1px solid #ddd",
    backgroundColor: "#f5f6f8",
    whiteSpace: "nowrap"
  };

  const tableCell = {
    border: "1px solid #ddd",
    verticalAlign: "top"
  };

  return (
    <>
      <div className="row pt-3">
        <div className="col-11 mx-3">
          {/* Clean Filter UI */}
          <Box
            sx={{
              width: "100%",
              marginBottom: "20px",
              padding: "20px",
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            <div className="row align-items-end">
              <div className="col-md-3 mb-3">
                <label className="form-label">
                  <strong>Account Name*</strong>
                </label>
                <Select
                  value={
                    companyName
                      ? { value: companyName, label: companyName }
                      : null
                  }
                  inputId="company_name"
                  name="company_name"
                  options={companyNameOptions}
                  onChange={handleInputChangeCompanyName}
                  placeholder={loadingCompany ? "Loading accounts..." : "Select Account"}
                  isDisabled={loadingCompany}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">
                  <strong>Location Name*</strong>
                </label>
                <Select
                  value={
                    locationID
                      ? {
                          value: locationID,
                          label: locationName
                        }
                      : null
                  }
                  inputId="location_name"
                  name="location_name"
                  options={locationNameOptions}
                  onChange={handleInputChangeLocationName}
                  placeholder={
                    loadingLocations
                      ? "Loading locations..."
                      : "Select Location"
                  }
                  isDisabled={!companyName || loadingLocations}
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">
                  <strong>From Date*</strong>
                </label>
                <DatePicker
                  size="large"
                  format="MM-DD-YYYY"
                  placeholder="mm-dd-yyyy"
                  style={{ width: "100%" }}
                  value={fromDate ? moment(fromDate, "YYYY-MM-DD") : null}
                  onChange={(date) => {
                    setFromDate(date ? date.format("YYYY-MM-DD") : "");
                    resetReport();
                  }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">
                  <strong>To Date*</strong>
                </label>
                <DatePicker
                  size="large"
                  format="MM-DD-YYYY"
                  placeholder="mm-dd-yyyy"
                  style={{ width: "100%" }}
                  value={toDate ? moment(toDate, "YYYY-MM-DD") : null}
                  onChange={(date) => {
                    setToDate(date ? date.format("YYYY-MM-DD") : "");
                    resetReport();
                  }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </div>

              <div className="col-md-2 mb-3 d-flex justify-content-end">
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleFetch}
                  disabled={loadingReport || loadingLocations}
                  sx={{
                    height: "40px",
                    minWidth: "110px",
                    textTransform: "none",
                    fontWeight: "bold"
                  }}
                >
                  {loadingReport ? "Fetching..." : "Fetch"}
                </Button>
              </div>
            </div>
          </Box>

          {loadingReport && (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 1 }}>Fetching report...</Typography>
            </Box>
          )}

          {!loadingReport && fetchClicked && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
                <Grid item xs={12} md={3}>
                  <Card sx={summaryCardStyle}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Account
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {reportData.account_name || companyName || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={summaryCardStyle}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {locationName || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {locationID || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Card sx={summaryCardStyle}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Total Clicks
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {reportData.total_clicks_account || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Card sx={summaryCardStyle}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Total Actions
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {reportData.total_actions_account || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Card sx={summaryCardStyle}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Date Range
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {formatISTDate(fromDate)}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {formatISTDate(toDate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        IST
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Location Level Table */}
              <TableContainer
                component={Paper}
                sx={{
                  width: "100%",
                  marginBottom: "30px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  border: "1px solid #ddd",
                  borderRadius: "10px"
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderCell}>Location Name</TableCell>
                      <TableCell sx={tableHeaderCell}>Location ID</TableCell>
                      <TableCell sx={tableHeaderCell}>Users</TableCell>
                      <TableCell sx={tableHeaderCell}>Actions</TableCell>
                      <TableCell sx={tableHeaderCell}>Clicks Consumed</TableCell>
                      <TableCell sx={tableHeaderCell}>First Click IST</TableCell>
                      <TableCell sx={tableHeaderCell}>Last Click IST</TableCell>
                      <TableCell sx={tableHeaderCell}>View</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {reportData.locations && reportData.locations.length > 0 ? (
                      reportData.locations.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell sx={tableCell}>
                            {row.location_name || locationName || "-"}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            {row.location_id || locationID || "-"}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            {row.user_count || 0}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            {row.total_actions || 0}
                          </TableCell>
                          <TableCell sx={{ ...tableCell, fontWeight: "bold" }}>
                            {row.total_clicks || 0}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            {formatISTDateTime(row.first_click_ist)}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            {formatISTDateTime(row.last_click_ist)}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleViewUsers(row)}
                              sx={{ textTransform: "none" }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ textAlign: "center", p: 3 }}>
                          No click data found for selected account, location, and date range.
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ ...tableCell, fontWeight: "bold" }}
                      >
                        Total Clicks
                      </TableCell>
                      <TableCell sx={{ ...tableCell, fontWeight: "bold" }}>
                        {reportData.total_clicks_account || 0}
                      </TableCell>
                      <TableCell colSpan={3} sx={tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
      </div>

      {/* User Breakdown Dialog */}
      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          User-Level Click Breakdown
          {selectedLocationRow ? (
            <Typography variant="body2" color="text.secondary">
              Location: {selectedLocationRow.location_name || locationName}
            </Typography>
          ) : null}
        </DialogTitle>

        <DialogContent>
          {userLoading ? (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 1 }}>Fetching user breakdown...</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ border: "1px solid #ddd" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCell}>Created By</TableCell>
                    <TableCell sx={tableHeaderCell}>Active Days</TableCell>
                    <TableCell sx={tableHeaderCell}>Actions</TableCell>
                    <TableCell sx={tableHeaderCell}>Clicks Consumed</TableCell>
                    <TableCell sx={tableHeaderCell}>Clicks Per Day / Times</TableCell>
                    <TableCell sx={tableHeaderCell}>Details</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {userRows && userRows.length > 0 ? (
                    userRows.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell sx={tableCell}>{user.created_by || "-"}</TableCell>
                        <TableCell sx={tableCell}>{user.active_days || 0}</TableCell>
                        <TableCell sx={tableCell}>{user.total_actions || 0}</TableCell>
                        <TableCell sx={{ ...tableCell, fontWeight: "bold" }}>
                          {user.total_clicks || 0}
                        </TableCell>
                        <TableCell sx={tableCell}>
                          {(user.daily_breakdown || []).map((day, dayIndex) => (
                            <Box key={dayIndex} sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                {formatISTDate(day.date)}: {day.total_clicks} clicks /{" "}
                                {day.total_actions} actions
                              </Typography>

                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                                {(day.times || []).map((time, timeIndex) => (
                                  <Chip
                                    key={timeIndex}
                                    label={time}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </TableCell>
                        <TableCell sx={tableCell}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleViewUserDetails(user)}
                            sx={{ textTransform: "none" }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: "center", p: 3 }}>
                        No user click details found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          Click Criteria Details
          {selectedUserRow ? (
            <Typography variant="body2" color="text.secondary">
              User: {selectedUserRow.created_by}
            </Typography>
          ) : null}
        </DialogTitle>

        <DialogContent>
          {detailsLoading ? (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 1 }}>Fetching criteria details...</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ border: "1px solid #ddd" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCell}>Created IST</TableCell>
                    <TableCell sx={tableHeaderCell}>Created By</TableCell>
                    <TableCell sx={tableHeaderCell}>Clicks</TableCell>
                    <TableCell sx={tableHeaderCell}>Rate Source</TableCell>
                    <TableCell sx={tableHeaderCell}>LOR</TableCell>
                    <TableCell sx={tableHeaderCell}>Rate From Date</TableCell>
                    <TableCell sx={tableHeaderCell}>Rate To Date</TableCell>
                    <TableCell sx={tableHeaderCell}>Pickup Time</TableCell>
                    <TableCell sx={tableHeaderCell}>Competition</TableCell>
                    <TableCell sx={tableHeaderCell}>Parity</TableCell>
                    <TableCell sx={tableHeaderCell}>Criteria</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {detailsRows && detailsRows.length > 0 ? (
                    detailsRows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={tableCell}>
                          {formatISTDateTime(row.createdAtIST)}
                        </TableCell>
                        <TableCell sx={tableCell}>{row.created_by || "-"}</TableCell>
                        <TableCell sx={{ ...tableCell, fontWeight: "bold" }}>
                          {row.clicks_consumed || 0}
                        </TableCell>
                        <TableCell sx={tableCell}>{row.rate_source || "-"}</TableCell>
                        <TableCell sx={tableCell}>{safeJoin(row.selected_lor)}</TableCell>
                        <TableCell sx={tableCell}>{row.from_date || "-"}</TableCell>
                        <TableCell sx={tableCell}>{row.to_date || "-"}</TableCell>
                        <TableCell sx={tableCell}>
                          {row?.automation_data?.pickup_time || "-"}
                        </TableCell>
                        <TableCell sx={tableCell}>
                          {row?.automation_data?.competitionSelected || "-"}
                        </TableCell>
                        <TableCell sx={tableCell}>
                          {row?.automation_data?.paritySelected || "-"}
                        </TableCell>
                        <TableCell sx={tableCell}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleViewCriteria(row)}
                            sx={{ textTransform: "none" }}
                          >
                            View Criteria
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ textAlign: "center", p: 3 }}>
                        No detailed criteria records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Criteria Dialog */}
      <Dialog
        open={criteriaDialogOpen}
        onClose={() => setCriteriaDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Selected Criteria</DialogTitle>

        <DialogContent>
          {selectedCriteriaRow ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography>
                  <strong>Created By:</strong> {selectedCriteriaRow.created_by || "-"}
                </Typography>
                <Typography>
                  <strong>Created IST:</strong>{" "}
                  {formatISTDateTime(selectedCriteriaRow.createdAtIST)}
                </Typography>
                <Typography>
                  <strong>Location:</strong>{" "}
                  {selectedCriteriaRow.location_name || locationName || "-"}
                </Typography>
                <Typography>
                  <strong>Location ID:</strong>{" "}
                  {selectedCriteriaRow.location_id || locationID || "-"}
                </Typography>
                <Typography>
                  <strong>Rate Source:</strong>{" "}
                  {selectedCriteriaRow.rate_source || "-"}
                </Typography>
                <Typography>
                  <strong>LOR Selected:</strong>{" "}
                  {safeJoin(selectedCriteriaRow.selected_lor)}
                </Typography>
                <Typography>
                  <strong>Rate Date Range:</strong>{" "}
                  {selectedCriteriaRow.from_date || "-"} to{" "}
                  {selectedCriteriaRow.to_date || "-"}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                Automation Criteria
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography>
                  <strong>Pickup Time:</strong>{" "}
                  {selectedCriteriaRow?.automation_data?.pickup_time || "-"}
                </Typography>
                <Typography>
                  <strong>Residency:</strong>{" "}
                  {selectedCriteriaRow?.automation_data?.residencySelected || "-"}
                </Typography>
                <Typography>
                  <strong>Competition:</strong>{" "}
                  {selectedCriteriaRow?.automation_data?.competitionSelected || "-"}
                </Typography>
                <Typography>
                  <strong>Parity:</strong>{" "}
                  {selectedCriteriaRow?.automation_data?.paritySelected || "-"}
                </Typography>
                <Typography>
                  <strong>Tether Checked:</strong>{" "}
                  {selectedCriteriaRow?.automation_data?.isCheckedTether
                    ? "Yes"
                    : "No"}
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ border: "1px solid #ddd" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderCell}>SIPP Code</TableCell>
                      <TableCell sx={tableHeaderCell}>Competitor Class 1</TableCell>
                      <TableCell sx={tableHeaderCell}>Competitor Class 2</TableCell>
                      <TableCell sx={tableHeaderCell}>Competitor Class 3</TableCell>
                      <TableCell sx={tableHeaderCell}>Strategy</TableCell>
                      <TableCell sx={tableHeaderCell}>Selected Competitor</TableCell>
                      <TableCell sx={tableHeaderCell}>Change Type</TableCell>
                      <TableCell sx={tableHeaderCell}>Amount</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {getCriteriaRows(selectedCriteriaRow).length > 0 ? (
                      getCriteriaRows(selectedCriteriaRow).map((criteria, index) => (
                        <TableRow key={index}>
                          <TableCell sx={tableCell}>{criteria.sipp_code || "-"}</TableCell>
                          <TableCell sx={tableCell}>{criteria.comp_sipp_code || "-"}</TableCell>
                          <TableCell sx={tableCell}>{criteria.comp_sipp_code2 || "-"}</TableCell>
                          <TableCell sx={tableCell}>{criteria.comp_sipp_code3 || "-"}</TableCell>
                          <TableCell sx={tableCell}>{criteria.strategy || "-"}</TableCell>
                          <TableCell sx={tableCell}>
                            {criteria.selected_competitor || "-"}
                          </TableCell>
                          <TableCell sx={tableCell}>
                            {criteria.selected_change || "-"}
                          </TableCell>
                          <TableCell sx={tableCell}>{criteria.amount || "-"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: "center", p: 2 }}>
                          No car-class criteria found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
